import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mic, Image, Users, FileAudio, Plus, ArrowRight, RefreshCw, Rss, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const syncRssFeed = async () => {
    setIsSyncing(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/rss/sync`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "RSS feed synced successfully!");
      fetchStats(); // Refresh stats after sync
    } catch (error) {
      toast.error("Failed to sync RSS feed");
    } finally {
      setIsSyncing(false);
    }
  };

  const statCards = [
    { 
      title: "Total Episodes", 
      value: stats?.total_episodes || 0, 
      icon: Mic, 
      color: "bg-[#2E8B8B]",
      link: "/admin/episodes"
    },
    { 
      title: "Published", 
      value: stats?.published_episodes || 0, 
      icon: FileAudio, 
      color: "bg-[#E67E22]",
      link: "/admin/episodes"
    },
    { 
      title: "Subscribers", 
      value: stats?.subscribers || 0, 
      icon: Users, 
      color: "bg-[#1F6666]",
      link: "/admin/subscribers"
    },
    { 
      title: "Media Files", 
      value: stats?.media_files || 0, 
      icon: Image, 
      color: "bg-[#D9731E]",
      link: "/admin/media"
    },
  ];

  const quickActions = [
    { label: "Sync Podbean", icon: Rss, action: syncRssFeed, color: "bg-[#4CAF50]", isSync: true },
    { label: "Add New Episode", icon: Mic, path: "/admin/episodes", color: "bg-[#2E8B8B]" },
    { label: "Upload Media", icon: Image, path: "/admin/media", color: "bg-[#E67E22]" },
    { label: "Update Settings", icon: Plus, path: "/admin/settings", color: "bg-[#1F6666]" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-semibold text-[#1A2323] mb-2">Dashboard</h1>
        <p className="text-[#4A5555]">Welcome back! Here's an overview of your podcast.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((stat, i) => (
          <Card 
            key={i} 
            className="border-[#E5E0D6] hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate(stat.link)}
            data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#4A5555] mb-1">{stat.title}</p>
                  <p className="text-3xl font-serif font-semibold text-[#1A2323]">
                    {isLoading ? "..." : stat.value}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card className="border-[#E5E0D6] mb-8">
        <CardHeader>
          <CardTitle className="font-serif text-xl text-[#1A2323]">Quick Actions</CardTitle>
          <CardDescription>Get started with common tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            {quickActions.map((action, i) => (
              <Button
                key={i}
                variant="outline"
                className="h-auto py-6 flex flex-col items-center gap-3 border-[#E5E0D6] hover:border-[#2E8B8B] hover:bg-[#2E8B8B]/5"
                onClick={() => action.isSync ? action.action() : navigate(action.path)}
                disabled={action.isSync && isSyncing}
                data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
              >
                <div className={`w-12 h-12 rounded-xl ${action.color} flex items-center justify-center`}>
                  {action.isSync && isSyncing ? (
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <action.icon className="w-6 h-6 text-white" />
                  )}
                </div>
                <span className="font-medium text-[#1A2323]">{action.label}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Getting Started */}
      <Card className="border-[#E5E0D6]">
        <CardHeader>
          <CardTitle className="font-serif text-xl text-[#1A2323]">Getting Started</CardTitle>
          <CardDescription>Complete these steps to set up your podcast website</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { step: 1, title: "Update Site Settings", desc: "Add your podcast description and host bio", path: "/admin/settings" },
              { step: 2, title: "Add Social Links", desc: "Connect your social media profiles", path: "/admin/social-links" },
              { step: 3, title: "Upload Episodes", desc: "Add your podcast episodes with audio and cover art", path: "/admin/episodes" },
            ].map((item) => (
              <div 
                key={item.step}
                className="flex items-center gap-4 p-4 rounded-lg bg-[#F5F2EB] hover:bg-[#E5E0D6]/50 cursor-pointer transition-colors"
                onClick={() => navigate(item.path)}
                data-testid={`getting-started-step-${item.step}`}
              >
                <div className="w-10 h-10 rounded-full bg-[#2E8B8B] text-white flex items-center justify-center font-semibold">
                  {item.step}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-[#1A2323]">{item.title}</p>
                  <p className="text-sm text-[#4A5555]">{item.desc}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-[#4A5555]" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
